import { User } from "oidc-client-ts";
import Logger from "@/utils/Logger";
import { AppSettingsService } from "./AppSettingsService";

export class TokenService {
  static extractAccessToken(user: User | null): string | null {
    if (!user) {
      Logger.debug("Token extraction skipped - no OIDC user object provided", [
        "TokenService",
      ]);
      return null;
    }

    if (user.access_token && !user.expired) {
      Logger.debug("Valid access token extracted from OIDC user", [
        "TokenService",
      ]);
      return user.access_token;
    }

    Logger.warn("Access token unavailable or expired", ["TokenService"]);
    return null;
  }

  static getDevelopmentToken(): string | null {
    if (
      !AppSettingsService.isOAuthLocalHostBypassEnabled() &&
      window.location.hostname === "localhost"
    ) {
      Logger.warn(
        "Development mode: OAuth bypass enabled -Bypassing authentication check for localhost and using mock access token for local testing",
        ["TokenService"],
      );
      return "dev-local-access-token";
    }

    Logger.debug("Development token not available - not localhost", [
      "TokenService",
    ]);
    return null;
  }

  static isTokenValid(user: User | null): boolean {
    const isValid = !!(user && !user.expired);
    Logger.debug(`Token validity check: ${isValid ? "valid" : "invalid"}`, [
      "TokenService",
    ]);
    return isValid;
  }

  static getTokenExpirationTime(user: User | null): number {
    const expirationTime = user?.expires_at || 0;
    Logger.debug("Token expiration time retrieved", ["TokenService"]);
    return expirationTime;
  }

  static isTokenExpiringSoon(
    user: User | null,
    warningTimeSeconds: number = 300,
  ): boolean {
    if (!user || !user.expires_at) {
      Logger.debug("Token expiry check skipped - insufficient data", [
        "TokenService",
      ]);
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = user.expires_at - currentTime;
    const isExpiringSoon = timeUntilExpiry <= warningTimeSeconds;

    if (isExpiringSoon) {
      Logger.warn("Token approaching expiration threshold", ["TokenService"]);
    } else {
      Logger.debug("Token expiry check completed", ["TokenService"]);
    }

    return isExpiringSoon;
  }
}
